
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import { configStore } from "@/store/store";
import ElementStudioWidthAdjustmentBar from "./ElementStudioWidthAdjustmentBar.vue";
import { makeWordPressAjaxRequest } from "@/lib/wordpress/ajax";
import ElementStudioTopbar from "./ElementStudioTopBar.vue";
import lodash from "lodash";

import {
  ElementStudioExpectedDataToLoad,
  CurrentlyEditingMacro,
  ElementStudioSaveLocation
} from "@/types/element-studio";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import defensiveCopy from "@/util/defensive-copy";
import ElementStudioTabsAndPanels from "@/components/ElementStudio/ElementStudioTabsAndPanels.vue";
import * as t from "io-ts";
import NotificationLogger from "@/lib/notifications/notifications";
import CodeInput from "@/components/Reusable/CustomInputs/CodeInput/CodeInput.vue";
import {
  compileTwigTemplate,
  getAllTwigMacrosString,
  TWIG_MACROS_CACHE_KEY
} from "@/lib/twig/twig";
import slugify from "slugify";
import { getMacroName } from "@/lib/elements/studio/element-to-php";
import {
  getItemNameFromSlug,
  getItemNamespaceFromSlug,
  isValidItemName,
  logNotificationOfSameItem
} from "@/lib/elements/studio/misc";
import Icon from "@/components/UI/Icon.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import { getSlugFromNamespaceAndFilename } from "@/lib/elements/studio/presets";
import { setTwigMacrosInStoreAndRecompile } from "@/lib/elements/studio/macros";
import ToggleInput from "@/components/Reusable/CustomInputs/ToggleInput.vue";

@Component({
  components: {
    ToggleInput,
    TextInput,
    Icon,
    CodeInput,
    ElementStudioTabsAndPanels,
    ElementStudioWidthAdjustmentBar,
    ElementStudioTopbar
  }
})
export default class ElementStudioMacros extends Vue {
  @Prop()
  value!: ElementStudioExpectedDataToLoad;

  lastSavedMacro: CurrentlyEditingMacro | false = false;
  currentlyEditingMacro: CurrentlyEditingMacro | false = false;

  saveInProgress = false;
  autoRefresh = true;

  activeTab = "macro";

  get macrosSlug() {
    return this.value.macros.map(macro => macro.slug);
  }

  isValidItemName(newName: string) {
    return isValidItemName(newName);
  }

  updateCurrentlyEditingMacro(newCurrentlyEditingMacro: CurrentlyEditingMacro) {
    this.currentlyEditingMacro = defensiveCopy(newCurrentlyEditingMacro);

    // Set the lastSavedMacro when opening a new element
    if (this.lastSavedMacro === false) {
      this.lastSavedMacro = defensiveCopy(newCurrentlyEditingMacro);
    }
  }

  updateTab(newTab: string) {
    this.activeTab = newTab;
  }

  updateMacroName(newName: string) {
    if (!this.currentlyEditingMacro) return;

    this.currentlyEditingMacro.macroData.slug = getSlugFromNamespaceAndFilename(
      getItemNamespaceFromSlug(this.currentlyEditingMacro.macroData.slug),
      newName
    );
  }

  updateAutoRefresh(newAutoRefresh: boolean) {
    this.autoRefresh = newAutoRefresh;

    // Don't wait for a new change, update all elements
    if (newAutoRefresh && this.currentlyEditingMacro) {
      this.updateMacroAndRecompileTwigTemplates(
        this.currentlyEditingMacro.macroData
      );
    }
  }

  // re-compiling the macros is very heavy. Debounce this aggressively
  throttledUpdateElement = lodash.debounce(
    this.updateMacroAndRecompileTwigTemplates,
    1000
  );

  updateMacroAndRecompileTwigTemplates(
    newMacro: CurrentlyEditingMacro["macroData"]
  ) {
    const twigMacros = defensiveCopy(configStore.twigMacros);

    const index = twigMacros.findIndex(macro => macro.slug === newMacro.slug);

    if (index === -1) {
      twigMacros.push(newMacro);

      const newValue = defensiveCopy(this.value);
      newValue.macros.push(newMacro);

      this.$emit("input", newValue);
    } else {
      twigMacros[index].macro = newMacro.macro;
    }

    // Delete the old macro if the slug changed and it's not a copy of the macro in a different save location
    if (
      this.lastSavedMacro &&
      this.lastSavedMacro.directoryPath &&
      newMacro.slug !== this.lastSavedMacro.macroData.slug &&
      newMacro.directoryPath === this.lastSavedMacro.directoryPath
    ) {
      this.deleteMacroFromServer(this.lastSavedMacro);
    }

    if (this.autoRefresh) {
      setTwigMacrosInStoreAndRecompile(twigMacros);
    }
  }

  deleteMacroAndRecompileTwigTemplates(deletedMacroSlug: string) {
    const twigMacros = defensiveCopy(configStore.twigMacros);
    const storeIndex = twigMacros.findIndex(
      macro => macro.slug === deletedMacroSlug
    );
    const valueIndex = this.value.macros.findIndex(
      macro => macro.slug === deletedMacroSlug
    );

    if (storeIndex !== -1) {
      twigMacros.splice(storeIndex, 1);
    }

    if (valueIndex !== -1) {
      const newValue = defensiveCopy(this.value);
      newValue.macros.splice(valueIndex, 1);

      this.$emit("input", newValue);
    }

    setTwigMacrosInStoreAndRecompile(twigMacros);
  }

  get currentMacroName() {
    if (!this.currentlyEditingMacro) return "";

    return getItemNameFromSlug(this.currentlyEditingMacro.macroData.slug);
  }

  get hasUnsavedChanges() {
    if (!this.currentlyEditingMacro || !this.lastSavedMacro) {
      return false;
    }

    return !lodash.isEqual(
      this.lastSavedMacro.macroData,
      this.currentlyEditingMacro.macroData
    );
  }

  updateMacroValue(newMacro: string) {
    if (!this.currentlyEditingMacro) return;

    this.currentlyEditingMacro.macroData.macro = newMacro;

    this.throttledUpdateElement(this.currentlyEditingMacro.macroData);
  }

  deleteMacro() {
    if (!this.currentlyEditingMacro) {
      return;
    }

    this.deleteMacroFromServer(this.currentlyEditingMacro, () => {
      this.currentlyEditingMacro = false;
    });
  }

  async deleteMacroFromServer(
    macroToDelete: CurrentlyEditingMacro,
    successCallback: () => void = () => {}
  ) {
    pipe(
      await makeWordPressAjaxRequest({
        action: "breakdance_delete_macro",
        dataToPost: {
          directoryPath: macroToDelete.directoryPath,
          filename: getItemNameFromSlug(macroToDelete.macroData.slug)
        },
        decoder: t.literal("success"),
        resultKey: "data"
      }),
      fold(
        () => {
          NotificationLogger.log.message("Failed to delete element");
        },
        () => {
          successCallback();

          this.deleteMacroAndRecompileTwigTemplates(
            macroToDelete.macroData.slug
          );
        }
      )
    );
  }

  discardChanges() {
    if (this.lastSavedMacro && this.lastSavedMacro.macroData) {
      this.updateMacroAndRecompileTwigTemplates(this.lastSavedMacro.macroData);
    }

    this.resetCurrentlyEditingMacro();
  }

  resetCurrentlyEditingMacro() {
    this.currentlyEditingMacro = false;
    this.lastSavedMacro = false;
    this.$emit("active", null);
  }

  openMacro(macroSlug: string) {
    const macroSaveLocation = defensiveCopy(
      this.value.macros.find(macro => macro.slug === macroSlug)
    );

    if (!macroSaveLocation) return;

    const macroString = configStore.twigMacros.find(
      macro => macro.slug === macroSlug
    )?.macro;

    if (!macroString) return;

    const activeSaveLocation = this.value.saveLocations.find(saveLocation =>
      macroSaveLocation.directoryPath.startsWith(saveLocation.directoryPath)
    );
    const onlyForAdvancedUsers = !!activeSaveLocation?.onlyForAdvancedUsers;

    this.updateCurrentlyEditingMacro({
      macroData: { ...macroSaveLocation, macro: macroString },
      directoryPath: macroSaveLocation.directoryPath,
      onlyForAdvancedUsers
    });
    this.$emit("active", "macro");
  }

  saveAs(saveLocation: ElementStudioSaveLocation) {
    if (!this.currentlyEditingMacro) return;

    const filename = getItemNameFromSlug(
      this.currentlyEditingMacro.macroData.slug
    );

    this.saveToServer(
      this.currentlyEditingMacro.macroData.macro,
      saveLocation.directoryPath,
      filename,
      getSlugFromNamespaceAndFilename(saveLocation.namespace, filename),
      saveLocation.onlyForAdvancedUsers
    );
  }

  saveNewItem(newItem: ElementStudioSaveLocation & { name: string }) {
    const macroName = getMacroName(newItem.namespace, newItem.name);
    const filename = slugify(newItem.name);
    const newSlug = getSlugFromNamespaceAndFilename(
      newItem.namespace,
      newItem.name
    );

    if (this.value.macros.find(macro => macro.slug === newSlug)) {
      logNotificationOfSameItem(this.value.saveLocations, newItem, "macro");

      return;
    }

    this.saveToServer(
      `{% macro ${macroName}() %}\n  {# Your code here #} \n{% endmacro %}`,
      newItem.directoryPath,
      filename,
      newSlug,
      newItem.onlyForAdvancedUsers
    );

    this.$emit("active", "macro");
  }

  saveOverride() {
    if (
      !this.currentlyEditingMacro ||
      !this.currentlyEditingMacro.directoryPath
    ) {
      return;
    }

    this.saveToServer(
      this.currentlyEditingMacro.macroData.macro,
      this.currentlyEditingMacro.directoryPath,
      getItemNameFromSlug(this.currentlyEditingMacro.macroData.slug),
      this.currentlyEditingMacro.macroData.slug,
      this.currentlyEditingMacro.onlyForAdvancedUsers
    );
  }

  async saveToServer(
    macro: string,
    directoryPath: string,
    filename: string,
    slug: string,
    onlyForAdvancedUsers: boolean
  ) {
    this.saveInProgress = true;

    pipe(
      await makeWordPressAjaxRequest({
        action: "breakdance_save_macro",
        dataToPost: {
          directoryPath,
          filename,
          macro
        },
        decoder: t.unknown
      }),
      fold(
        e => {
          console.error(e);
          NotificationLogger.log.message(
            "Failed to save element, check console"
          );
        },
        () => {
          const newMacro: CurrentlyEditingMacro = {
            macroData: {
              macro,
              slug,
              directoryPath
            },
            directoryPath,
            onlyForAdvancedUsers
          };

          this.updateCurrentlyEditingMacro(newMacro);
          this.updateMacroAndRecompileTwigTemplates(newMacro.macroData);
          this.lastSavedMacro = newMacro;
        }
      )
    );

    this.saveInProgress = false;
  }
}
